import { HttpBackend, HttpClient, HttpHeaders } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { MsalGuardConfiguration, MsalService, MSAL_GUARD_CONFIG } from "@azure/msal-angular";
import { AccountInfo, PopupRequest, SilentRequest } from "@azure/msal-browser";
import { catchError, concatMap, delayWhen, map, Observable, of } from "rxjs";
import { AuthService } from "../authentication/auth.service";
import { GraphArray, GraphBatchResponses, GraphPhotoSizes, GraphPresence, GraphUserLite } from "./graph.models";

@Injectable()
export class GraphService
{
	private http: HttpClient;
	private graphApiUrl: string = 'https://graph.microsoft.com/v1.0/';

	// constructor
	constructor
		(
			private handler: HttpBackend,

			// azure AD
			@Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
			private authService: MsalService
		)
	{
		this.http = new HttpClient(handler);
	}

	// get me photo
	getMePhoto(size?: GraphPhotoSizes): Observable<string>
	{
		return this.buildAuthorizedRequest((headers) =>
		{
			headers = headers.append('Content-Type', 'image/jpg');

			let apiUrl = ''
			if (size && size != GraphPhotoSizes.Original)
				apiUrl = `${this.graphApiUrl}me/photos/${size}/$value`;
			else
				apiUrl = `${this.graphApiUrl}me/photo/$value`;

			return this.http.get(apiUrl, { headers: headers, responseType: 'blob' })
				.pipe(
					// convert: blob to base64
					concatMap(result => this.buildBlobConvertObservable(result)),
					// trim base64 prefix
					map(result => result ? result.split(',')[1] : null)
				);
		});
	}

	// get me presence
	getMePresence(): Observable<GraphPresence>
	{
		return this.buildAuthorizedRequest((headers) =>
		{
			return this.http.get<GraphPresence>(`${this.graphApiUrl}me/presence`, { headers: headers, responseType: 'json' });
		});
	}

	// get me id
	getMeId(): Observable<string>
	{
		return this.buildAuthorizedRequest((headers) =>
		{
			return this.http.get<GraphUserLite>(`${this.graphApiUrl}me?$select=id,userPrincipalName`, { headers: headers, responseType: 'json' })
				.pipe(map(result => result?.id || null ));
		});
	}

	// get user photo
	getUserPhoto(identifier: string, size?: GraphPhotoSizes): Observable<string>
	{
		return this.buildAuthorizedRequest((headers) =>
		{
			headers = headers.append('Content-Type', 'image/jpg');

			let apiUrl = ''
			if (size && size != GraphPhotoSizes.Original)
				apiUrl = `${this.graphApiUrl}users/${identifier}/photos/${size}/$value`;
			else
				apiUrl = `${this.graphApiUrl}users/${identifier}/photo/$value`;

			return this.http.get(apiUrl, { headers: headers, responseType: 'blob' })
				.pipe(
					// convert: blob to base64
					concatMap(result => this.buildBlobConvertObservable(result)),
					// trim base64 prefix
					map(result => result ? result.split(',')[1] : null)
				);
		});
	}

	// get user presence
	getUserPresence(id: string): Observable<GraphPresence>
	{
		return this.buildAuthorizedRequest((headers) =>
		{
			return this.http.get<GraphPresence>(`${this.graphApiUrl}users/${id}/presence`, { headers: headers, responseType: 'json' });
		});
	}

	// get users presence
	getUsersPresence(ids: string[]): Observable<GraphArray<GraphPresence>>
	{
		return this.buildAuthorizedRequest((headers) =>
		{
			headers = headers.append('Content-Type', 'application/json');

			const body = { ids: ids };
			return this.http.post<GraphArray<GraphPresence>>(`${this.graphApiUrl}communications/getPresencesByUserId`, body, { headers: headers, responseType: 'json' });
		});
	}

	// get users azure ids
	getUsersIds(userPrincipals: string[]): Observable<GraphArray<GraphUserLite>>
	{
		return this.buildAuthorizedRequest((headers) =>
		{
			headers = headers.append('ConsistencyLevel', 'eventual');

			var params = userPrincipals.map(function (x)
			{
				return `'${x}'`;
			}).join(',');

			return this.http.get<GraphArray<GraphUserLite>>(`${this.graphApiUrl}users?$select=id,userPrincipalName&$filter=userPrincipalName in (${params})`, { headers: headers, responseType: 'json' });
		});
	}

	// batch users photo
	batchUsersPhoto(identifiers: string[], size?: GraphPhotoSizes): Observable<GraphBatchResponses>
	{
		return this.buildAuthorizedRequest((headers) =>
		{
			headers = headers.append('Content-Type', 'application/json');

			let requests = [];

			// cycle identifiers
			for (let i = 0; i < identifiers.length; i++)
			{
				if (identifiers[i])
				{
					let url = null;
					if (size && size != GraphPhotoSizes.Original)
						url = `/users/${identifiers[i]}/photos/${size}/$value`;
					else
						url = `/users/${identifiers[i]}/photo/$value`;

					requests.push(this.buildBatchArgumentRequest(identifiers[i], 'GET', url, { 'Content-Type': 'image/jpg' }));
				}
			}

			const body = { requests: requests };
			return this.http.post<GraphBatchResponses>(`${this.graphApiUrl}$batch`, body, { headers: headers, responseType: 'json' });
		});
	}

	// ----- UTILITY Methods -----

	// build batch argument request
	private buildBatchArgumentRequest(id: string, method: string, url: string, headers?: any, body?: any): any
	{
		return {
			id: id,
			method: method,
			url: url,
			body: body,
			headers: headers
		};
	}

	// build blob to base64 convert observable
	private buildBlobConvertObservable(file: Blob): Observable<string>
	{
		return new Observable<string>(subscriber =>
		{
			// Blob to base64 string
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => { subscriber.next(reader.result as string); subscriber.complete(); };
			reader.onerror = () => subscriber.error(reader.error);
			return () => reader.abort(); // cancel function in case you unsubscribe from the obs
		});
	}

	// ----- AUTHORIZATION Methods -----

	// build authorized request using azure access token
	private buildAuthorizedRequest(request: (headers: HttpHeaders) => Observable<any>): Observable<any>
	{
		const azureToken = AuthService.getAzureAdAccessToken();

		// check: azure access token not found or expired - acquire new token
		if (!azureToken || this.tokenExpired(azureToken))
		{
			let account: AccountInfo;
			if (!!this.authService.instance.getActiveAccount())
			{
				// active account selected
				account = this.authService.instance.getActiveAccount();
			}
			else
			{
				// no active account, fallback to first account
				account = this.authService.instance.getAllAccounts()[0];
			}

			return this.authService.acquireTokenSilent({ ...this.msalGuardConfig.authRequest, account } as SilentRequest)
				.pipe(
					concatMap(response =>
					{
						// saving azure ad auth token to local storage
						AuthService.setAzureAdAccessToken(response.accessToken);

						return request(new HttpHeaders().append('Authorization', `Bearer ${response.accessToken}`));
					}),
					catchError(error =>
					{
						console.log(error);
						return of(null);
					})
				);
		}
		else
		{
			return request(new HttpHeaders().append('Authorization', `Bearer ${azureToken}`));
		}
	}

	// check: expired token
	private tokenExpired(token: string)
	{
		const expiry = (JSON.parse(atob(token.split('.')[1]))).exp;
		return (Math.floor((new Date).getTime() / 1000)) >= expiry;
	}
}